/**
 * Site requests tab (22 #7): by default the queue grouped by urgency — Overdue · Due this week · Later —
 * with overdue requests leading in red (they never drop out). The calendar is an optional view (`?view=calendar`)
 * that shows the same queue next to the month and the picked day. Rows carry the hover actions.
 */
import { useMemo, useState } from 'react';
import { CalendarDays, CloudOff } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, formatNumber, parseDateValue, toDateInputValue } from '@/lib/format';
import { useUrlParam } from '@/hooks/useUrlState';
import { CardHead, ChipSelect, Segmented, SfCard, plural } from './SfUi';
import { SiteRequestGroups, SiteRequestList, SiteRequestTip } from './SiteRequestList';

export const SITE_REQUEST_VIEWS = ['list', 'calendar'];

/** A business day ('YYYY-MM-DD', read in Dubai time) as the browser-local midnight the calendar works in. */
const localDate = (isoDay) => {
  const parsed = parseDateValue(isoDay);
  if (!parsed) return null;
  const [y, m, d] = toDateInputValue(isoDay).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const pad2 = (value) => String(value).padStart(2, '0');
/**
 * The calendar's picked day back to 'YYYY-MM-DD' from its own (browser-local) date parts. Converting it
 * through the Dubai time zone instead would land on the previous day in browsers east of Dubai.
 */
export const calendarDayIso = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/**
 * props: requests (normalized), projects, canEdit, onChanged, onOpenProjects,
 * projectId + onProjectChange (the Project filter, kept by the page so Export matches the view),
 * error + onRetry (the requests could not be loaded: say so instead of an empty list).
 * Wave 3: selection ({ selectedIds, onToggle } — check boxes for "Create LPO draft"), onTrace(request) ("Where is
 * it?") and belowList (the bulk bar and the drafts, shown under the list).
 */
export function SiteRequestsPanel({ requests, projects, canEdit, onChanged, onOpenProjects, projectId: projectIdProp = null, onProjectChange, error = '', onRetry, selection = null, onTrace, belowList = null }) {
  // Opens on today in Dubai, as a local-midnight date the calendar can highlight.
  const [selected, setSelected] = useState(() => localDate(toDateInputValue(new Date())) || new Date());
  const [ownProjectId, setOwnProjectId] = useState(null);
  const [viewParam, setView] = useUrlParam('view', 'list');
  const view = SITE_REQUEST_VIEWS.includes(viewParam) ? viewParam : 'list';
  const projectId = onProjectChange ? projectIdProp : ownProjectId;
  const setProjectId = onProjectChange || setOwnProjectId;

  const visible = useMemo(
    () => (projectId ? requests.filter((request) => request.project_id === projectId) : requests),
    [requests, projectId]
  );
  const dates = useMemo(() => visible.map((request) => localDate(request.due_date)).filter(Boolean), [visible]);
  const selectedIso = calendarDayIso(selected);
  const dayQueue = useMemo(() => visible.filter((request) => toDateInputValue(request.due_date) === selectedIso), [visible, selectedIso]);
  const projectOptions = useMemo(() => {
    const counts = new Map();
    requests.forEach((request) => counts.set(request.project_id, (counts.get(request.project_id) || 0) + 1));
    return projects
      .filter((project) => counts.has(project.id))
      .map((project) => ({ value: project.id, label: project.name, count: counts.get(project.id) }));
  }, [projects, requests]);

  if (error) {
    return (
      <SfCard data-testid="site-requests-all">
        <CardHead title="Open site requests" />
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load site requests" text={error}
          action={onRetry ? <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button> : null} />
      </SfCard>
    );
  }

  const overdue = visible.filter((request) => request.urgency === 'overdue').length;
  const listCard = (
    <SfCard data-testid="site-requests-all">
      <CardHead
        title="Open site requests"
        sub={`${plural(visible.length, 'request')}${overdue ? ` · ${formatNumber(overdue)} overdue` : ''} · most urgent first`}
        right={(
          <>
            <ChipSelect label="Project" value={projectId} options={projectOptions} onChange={setProjectId} align="end" emptyText="No project has open requests" testId="site-requests-project" />
            <Segmented
              size="sm"
              ariaLabel="Site requests view"
              value={view}
              onChange={setView}
              testId="site-requests-view"
              options={[{ key: 'list', label: 'List', testId: 'site-requests-view-list' }, { key: 'calendar', label: 'Calendar', testId: 'site-requests-view-calendar' }]}
            />
          </>
        )}
      />
      <SiteRequestGroups requests={visible} canEdit={canEdit} onChanged={onChanged} selection={selection} onTrace={onTrace} emptyAction={onOpenProjects ? <Button variant="outline" size="sm" onClick={onOpenProjects}>Open Projects</Button> : null} />
      {belowList}
      {visible.length ? <SiteRequestTip onOpenProjects={onOpenProjects} /> : null}
    </SfCard>
  );

  if (view !== 'calendar') return listCard;

  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      {listCard}
      <SfCard data-testid="site-requests-calendar">
        <CardHead icon={CalendarDays} title="Calendar" sub="Highlighted days have requests due" />
        <div className="flex justify-center px-2 py-3">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(value) => value && setSelected(value)}
            modifiers={{ requested: dates }}
            modifiersClassNames={{ requested: 'bg-status-amber-bg font-bold text-status-amber-ink' }}
            className="w-fit"
          />
        </div>
        <div className="border-t border-line-2">
          <p className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 sm:px-[18px]">
            Due {formatDate(selectedIso)} · {dayQueue.length}
          </p>
          {dayQueue.length ? (
            <SiteRequestList requests={dayQueue} canEdit={canEdit} onChanged={onChanged} testId="site-requests-day" rowTestPrefix="day-" onTrace={onTrace} />
          ) : (
            <p className="px-4 pb-4 text-[12.5px] text-ink-3 sm:px-[18px]">Nothing due on this day.</p>
          )}
        </div>
      </SfCard>
    </div>
  );
}
